import React from 'react';

import { useSparkRunStore, useStatusBarStore, useUserStore } from 'stores';
import ApplyKnowledge from './ApplyKnowledge';
import { useWorkspaceRecommendedMemories } from 'hooks/Workspace/useWorkspaceRecommendedMemories';
import SparkSurveyForm, {
  restoreDataFromLocalStorage,
} from 'external/SurveyJs/SparkSurveyForm';
import { useStartSparkRun } from 'hooks/SparkRuns';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useFormByID } from 'hooks/Forms';
import { useSparksByID } from 'hooks/spark';
import { useSparkPrivacyTypes } from 'hooks/EntityTypes/useSparkPrivacyTypes';
import AuthenticationModal from './AuthenticationModal';
import { useDialog, useWorkspaceContext } from 'hooks/common';
import { CompletingEvent } from 'survey-core';
import { isNullOrUndefined, parseJSONCleaned } from 'utilities/index';
import SparkRunByIDSkeleton from 'sharedComponents/Skeleton/SparkRunByIDSkeleton';
import type { SparkRunInputParameterRecommendation } from 'types/Spark/SparkRunInputParameterRecommendations';
import { Box, GridProps } from '@mui/material';

export type SparkRunFormValues = {
  applyKnowledge?: boolean;
} & Record<string, string>;

type SparkRunScreenPropTypes = {
  workspaceID: string | null | undefined;
  formID?: string;
  addToSparkRunIDsCollection: (sparkRunID: string) => void;
  actionContainerStyles?: GridProps['sx'];
};

function SparkRunPageForm({
  workspaceID,
  formID,
  addToSparkRunIDsCollection,
  actionContainerStyles,
}: SparkRunScreenPropTypes) {
  const navigate = useNavigate();
  const { state, pathname } = useLocation();

  const closeFormSideBar = useSparkRunStore((state) => state.closeFormSideBar);
  const setSelectedSparkRunID = useSparkRunStore(
    (state) => state.setSelectedSparkRunID
  );

  const [applyKnowledge, setApplyKnowledge] = React.useState(false);
  const [isApplyingKnowledge, setIsApplyingKnowledge] = React.useState(false);

  const inputValueRef = React.useRef<Record<string, string | number>>();
  const { id: sparkID } = useParams();

  const { joinedSpark, isLoading: isLoadingSpark } = useSparksByID(sparkID);
  const { form, isLoading: isLoadingForm } = useFormByID(
    joinedSpark?.attributes.inputFormID
  );
  const { sparkPrivacyTypeIdByNameMap, isLoading: isLoadingPrivacy } =
    useSparkPrivacyTypes();
  const { recommendedMemories, isLoading: isLoadingMemories } =
    useWorkspaceRecommendedMemories(workspaceID);

  const updateStatues = useStatusBarStore((state) => state.updateStatues);
  const setStatusConfig = useStatusBarStore((state) => state.setStatusConfig);
  const isSparkRunning = useStatusBarStore((state) => state.isRunning);

  const [recommendations, setRecommendations] = React.useState<
    SparkRunInputParameterRecommendation[] | null
  >(null);

  const currentUserID = useUserStore(
    React.useCallback((state) => state.currentUserID, [])
  );

  const { currentWorkspaceID } = useWorkspaceContext();

  const { mutateAsync: startSparkRun, isLoading: isStartingSparkRun } =
    useStartSparkRun({
      sparkID,
    });

  const { open, closeDialog, openDialog } = useDialog();

  const handleRunSpark = React.useCallback(async () => {
    updateStatues({
      isAttempted: true,
      isComplete: false,
      isRunning: true,
      isVisible: true,
    });
    setStatusConfig({
      statusBarType: 'sparkRun',
      secondaryStatusID: sparkID,
    });
    closeFormSideBar();
    if (!inputValueRef.current) {
      return;
    }
    try {
      const response = await startSparkRun({
        formInput: JSON.stringify(inputValueRef.current),
      });

      const sparkRunID = response.data.id;

      const usageRights = response.data.meta.user.usageRights;
      const requiresPayment = usageRights?.requiresPayment;
      updateStatues({ isPendingPayment: requiresPayment });
      setStatusConfig({
        statusBarID: sparkRunID,
      });
      if (requiresPayment) {
        window.open(usageRights?.stripe?.checkout.paymentURL);
        return;
      }
      addToSparkRunIDsCollection(sparkRunID);
      setSelectedSparkRunID(sparkRunID);
    } catch {
      updateStatues({ isError: true });
    }
  }, [
    closeFormSideBar,
    setSelectedSparkRunID,
    addToSparkRunIDsCollection,
    setStatusConfig,
    sparkID,
    startSparkRun,
    updateStatues,
  ]);

  React.useEffect(() => {
    if (!inputValueRef.current) {
      inputValueRef.current =
        parseJSONCleaned<Record<string, string>>(
          restoreDataFromLocalStorage(sparkID || '') || ''
        ) || {};
    }
    // This makes sure to check if we should auto re-run the spark.
    if (isNullOrUndefined(state?.initiateRun) || !workspaceID) {
      return;
    }

    if (state?.initiateRun) {
      handleRunSpark();
      navigate(pathname, {});
    }
  }, [handleRunSpark, navigate, pathname, sparkID, state, workspaceID]);

  const handleSparkRunPermissions = (completingEvent: CompletingEvent) => {
    if (!!currentUserID && !!currentWorkspaceID) {
      completingEvent.allowComplete = true;
      return;
    }

    if (!joinedSpark || !sparkPrivacyTypeIdByNameMap) {
      return;
    }

    const privacyTypeID = joinedSpark.attributes.sparkPrivacyTypeID;
    switch (privacyTypeID) {
      case +sparkPrivacyTypeIdByNameMap['Public']:
        openDialog();
        break;
      case +sparkPrivacyTypeIdByNameMap['Private']:
        openDialog();
        break;
      case +sparkPrivacyTypeIdByNameMap['Anyone with a link']:
        openDialog();
        break;
      default:
        completingEvent.allowComplete = false;
    }
    completingEvent.allowComplete = false;
  };

  const updateInputValueRef = (formValues: Record<string, string | number>) => {
    inputValueRef.current = formValues;
  };

  const handleValueChange = (
    valueChangedEvent: Record<string, string | number>
  ) => {
    updateInputValueRef(valueChangedEvent);
  };

  const handleApplyKnowLedge = (
    newRecommendations: SparkRunInputParameterRecommendation[]
  ) => {
    if (!!recommendations) {
      return;
    }
    setRecommendations(newRecommendations);
  };
  const isFormDisabled =
    isStartingSparkRun || isSparkRunning || isApplyingKnowledge;

  if (
    isLoadingSpark ||
    isLoadingForm ||
    isLoadingPrivacy ||
    isLoadingMemories
  ) {
    return <SparkRunByIDSkeleton renderHeaderSkeleton={false} count={3} />;
  }

  return (
    <>
      {!!currentUserID && !!workspaceID && sparkID && (
        <ApplyKnowledge
          recommendedMemories={recommendedMemories}
          workspaceID={workspaceID}
          sparkID={sparkID}
          onApplyKnowledge={handleApplyKnowLedge}
          setApplyKnowledge={setApplyKnowledge}
          applyKnowledge={applyKnowledge}
          setIsApplyingKnowledge={setIsApplyingKnowledge}
        />
      )}
      <SparkSurveyForm
        onComplete={handleRunSpark}
        formConfigJSON={form?.attributes.config || ''}
        showProgressBar='off'
        onCompleting={handleSparkRunPermissions}
        onValueChanged={handleValueChange}
        id={formID || sparkID || ''}
        disabled={isFormDisabled || false}
        recommendations={applyKnowledge ? recommendations : undefined}
        floatingAction={true}
        actionContainerStyles={{
          ...actionContainerStyles,
        }}
        formContainerStyles={{
          '& .sd-container-modern': {
            marginBottom: '5rem',
          },
          '& .sd-root-modern': {
            bgcolor: 'transparent !important',
          },
          '& .sd-body.sd-body--static': {
            marginX: 0,
            paddingTop: '16px',
            maxWidth: 'unset',
          },
          '& .sd-root-modern.sd-root-modern--full-container': {
            scrollbarWidth: 'none',
          },

          '& .sd-body.sd-body--static .sd-body__timer, .sd-body.sd-body--static .sd-body__navigation, .sd-body.sd-body--static .sd-body__page':
            {
              paddingX: '1px',
            },
          '& .sd-root-modern--mobile .sd-body.sd-body--responsive, .sd-root-modern--mobile .sd-body.sd-body--static':
            {
              paddingX: '1px',
            },
          '& .sd-question__header': {
            pointerEvents: 'none',
          },
        }}
      />
      <Box
        className='run_page_shadow'
        sx={{
          background:
            'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 1))',
          height: '80px',
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
        }}
      />
      {open && (
        <AuthenticationModal
          open={open}
          onClose={closeDialog}
          callBack={() => {
            navigate(pathname, {
              state: { initiateRun: true },
            });
          }}
        />
      )}
    </>
  );
}

export default SparkRunPageForm;
